import { Flex, SimpleGrid, Text } from '@chakra-ui/react';
import ResourceText from '../../../components/resource-text/ResourceText';
import { Resource } from '../../../types/markets/types';

const isValidUrl = (val: unknown): val is string =>
  typeof val === 'string' && val.startsWith('http');

const isPlainText = (val: unknown): val is string =>
  typeof val === 'string' &&
  !val.startsWith('http') &&
  val.trim().length > 0;

const keyToTitle = (key: string) => {
  // Handle special cases first
  if (key === '911Protocol') return '911 Protocol';
  if (key === 'PhysicianDirectoryLink')
    return 'Physician Directory Link';
  if (key === 'switchboardOperatorNumber')
    return 'Switchboard Operator Number';
  if (key === 'patientCaseSentToPACResponses')
    return 'Patient Case Sent To PAC Responses';
  if (key === 'covidSchedulingWithoutScreening')
    return 'Covid Scheduling Without Screening';
  if (key === 'covidScreeningRequired')
    return 'Covid Screening Required';
  if (key === 'interpreterLineAndCode')
    return 'Interpreter Line And Code';
  if (key === 'patientOutreachCampaigns')
    return 'Patient Outreach Campaigns';
  if (key === 'centralBillingNumber') return 'Central Billing Number';
  if (key === 'patientPortalFinder') return 'Patient Portal Finder';
  if (key === 'athenaDowntimeForm') return 'Athena Downtime Form';
  if (key === 'trainingFolder') return 'Training Folder';
  if (key === 'issuesForm') return 'Issues Form';
  if (key === 'homePage') return 'Home Page';
  if (key === 'jobAids') return 'Job Aids';
  if (key === 'urgentCare') return 'Urgent Care';

  // Check if key is all caps (likely an acronym) and preserve it
  // Handle both with and without underscores
  // Remove length restriction to allow multi-word acronyms
  const isAllCaps =
    key === key.toUpperCase() &&
    /^[A-Z0-9_]+$/.test(key);

  if (isAllCaps) {
    // Convert underscores back to spaces for display
    return key.replace(/_/g, ' ');
  }

  // For custom/unknown keys, use improved camelCase parsing
  // Handle cases like "PACLead" where "PAC" should stay as one word
  const words: string[] = [];
  let currentWord = '';
  let inAcronym = false; // Track if we're in an acronym (consecutive uppercase)

  for (let i = 0; i < key.length; i++) {
    const char = key[i];
    const isUpperCase = /[A-Z]/.test(char);
    const isLowerCase = /[a-z]/.test(char);
    const nextChar = i < key.length - 1 ? key[i + 1] : null;
    const nextIsLowerCase = nextChar ? /[a-z]/.test(nextChar) : false;

    if (isUpperCase) {
      if (currentWord.length === 0) {
        // Start of key - begin new word
        currentWord = char;
        // Check if this starts an acronym (multiple uppercase) or single word
        inAcronym = nextChar ? /[A-Z]/.test(nextChar) : false;
      } else if (isLowerCase && /[a-z]/.test(currentWord[currentWord.length - 1])) {
        // Transition from lowercase to uppercase - start new word
        words.push(currentWord);
        currentWord = char;
        inAcronym = nextChar ? /[A-Z]/.test(nextChar) : false;
      } else if (inAcronym && isUpperCase && nextIsLowerCase) {
        // Uppercase in acronym but next is lowercase - end acronym, start new word
        words.push(currentWord);
        currentWord = char;
        inAcronym = false;
      } else if (inAcronym && isUpperCase) {
        // Continue acronym (consecutive uppercase)
        currentWord += char;
      } else if (!inAcronym && isUpperCase) {
        // New uppercase after lowercase - start new word
        words.push(currentWord);
        currentWord = char;
        inAcronym = nextChar ? /[A-Z]/.test(nextChar) : false;
      } else {
        currentWord += char;
      }
    } else if (isLowerCase) {
      if (inAcronym && currentWord.length > 1) {
        // Acronym ended, start new word with this lowercase
        words.push(currentWord);
        currentWord = char;
        inAcronym = false;
      } else if (inAcronym && currentWord.length === 1) {
        // Single uppercase followed by lowercase - not an acronym, continue word
        currentWord += char;
        inAcronym = false;
      } else {
        // Continue current word
        currentWord += char;
      }
    } else {
      // Numbers or other characters
      currentWord += char;
    }
  }
  if (currentWord.length > 0) {
    words.push(currentWord);
  }

  // Reconstruct title with proper spacing and capitalization
  return words
    .map((word, index) => {
      // Check if word is all caps (preserve acronyms like "PAC")
      const isAllCaps = /^[A-Z0-9]+$/.test(word) && word === word.toUpperCase();
      if (isAllCaps && word.length > 1) {
        // Preserve all-caps acronyms exactly
        return word;
      } else if (index === 0) {
        // First word: capitalize first letter only
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
      // Subsequent words: preserve capitalization
      return word;
    })
    .join(' ')
    .trim();
};

type Props = {
  resources: Resource;
};

const MarketResourceLinks = ({ resources }: Props) => {
  const items = Object.entries(resources).map(([key, value]) => {
    const title = keyToTitle(key);

    if (isValidUrl(value)) {
      return (
        <Flex key={key} justify="flex-start">
          <ResourceText title={title} url={value} />
        </Flex>
      );
    }

    if (isPlainText(value)) {
      return (
        <Flex key={key} justify="flex-start">
          <ResourceText title={title} plainText={value} />
        </Flex>
      );
    }

    if (typeof value === 'object' && value !== null) {
      const entries = Object.entries(value);
      const subLinks = entries.map(([subKey, subValue]) => ({
        title: subKey + (isValidUrl(subValue) ? '' : `: ${subValue}`),
        url: isValidUrl(subValue) ? subValue : undefined,
      }));

      return (
        <Flex key={key} justify="flex-start">
          <ResourceText title={title} subLinks={subLinks} />
        </Flex>
      );
    }

    return null;
  });

  return (
    <SimpleGrid columns={2} spacing="1rem" width="100%">
      {items.filter(Boolean).length > 0 ? (
        items
      ) : (
        <Text fontSize="sm" color="gray.500">
          No resource links available.
        </Text>
      )}
    </SimpleGrid>
  );
};

export default MarketResourceLinks;
